/**
 * Slownictwo oceny wg metodyki, wspolne dla serwera i klienta.
 *
 * Zyje w shared/ z tego samego powodu co contracts.ts: obie strony maja
 * patrzec na te same wartosci, a nie przepisywac ich recznie. Wylacznie
 * stale, typy i male czyste funkcje. Zero DOM, zero API Node.
 *
 * W bazie wszystkie te pola to zwykle Stringi (schema.prisma), wiec to jest
 * jedyne miejsce, ktore pilnuje, jakie wartosci wolno wpisac.
 */

// ---------------------------------------------------------------------------
// Rodzaj wejscia
// ---------------------------------------------------------------------------

/** AnalysisSession.inputKind. AUDIO to nagranie (jak dotad), TEXT to tekst wgrany albo wklejony. */
export const RODZAJE_WEJSCIA = ["AUDIO", "TEXT"] as const;
export type InputKind = (typeof RODZAJE_WEJSCIA)[number];

export function czyRodzajWejscia(wartosc: unknown): wartosc is InputKind {
  return typeof wartosc === "string" && (RODZAJE_WEJSCIA as readonly string[]).includes(wartosc);
}

// ---------------------------------------------------------------------------
// Statusy sesji
// ---------------------------------------------------------------------------

/**
 * Wszystkie statusy AnalysisSession.status, w kolejnosci cyklu zycia.
 *
 * SPLITTING i SPLIT_REVIEW dotycza wylacznie tekstu BEZ podpisow mowcow: model
 * dzieli tekst na wypowiedzi (SPLITTING), potem sesja czeka na czlowieka, ktory
 * poprawia podzial i go zatwierdza (SPLIT_REVIEW). Nagranie i tekst z podpisami
 * ich nie przechodza.
 *
 * Ta lista musi sie zgadzac z komentarzem przy polu `status` w
 * prisma/schema.prisma. Pilnuje tego tests/unit/sessionStatus.test.ts (przez
 * client/src/lib/sessionStatus.ts, ktory ja re-eksportuje).
 */
export const STATUSY_SESJI = [
  "QUEUED",
  "PROBING",
  "TRANSCRIBING",
  "PROSODY",
  "EMOTIONS",
  "SPLITTING",
  "SPLIT_REVIEW",
  "LENSES",
  "DONE",
  "NO_SPEECH",
  "FAILED",
] as const;
export type SessionStatus = (typeof STATUSY_SESJI)[number];

/** Statusy, w ktorych sesja nie zmieni sie sama: koniec pracy albo awaria. */
export const STATUSY_KONCOWE: ReadonlySet<string> = new Set(["DONE", "NO_SPEECH", "FAILED"]);

/**
 * Status, w ktorym sesja czeka na czlowieka, a nie na worker. Ponawianie z kolejki
 * nie ma prawa go ruszyc ani cofnac do QUEUED.
 */
export const STATUS_CZEKA_NA_CZLOWIEKA: SessionStatus = "SPLIT_REVIEW";

// ---------------------------------------------------------------------------
// Rodzaj wskazania
// ---------------------------------------------------------------------------

/**
 * LensFinding.kind. Te same wartosci co pole `rodzaj` znacznika w
 * methodology/lenses/*.json i pole `rodzaj` w odpowiedzi modelu.
 */
export const RODZAJE_WSKAZAN = ["mocna-strona", "do-poprawy"] as const;
export type FindingKind = (typeof RODZAJE_WSKAZAN)[number];

export function czyRodzajWskazania(wartosc: unknown): wartosc is FindingKind {
  return typeof wartosc === "string" && (RODZAJE_WSKAZAN as readonly string[]).includes(wartosc);
}

/**
 * LensFinding.blockKey jest kolumna WYMAGANA, odziedziczona po starym ksztalcie
 * wskazania (blok "highlights" z nigdy niezbudowanego formatu soczewek). Nowy
 * silnik nie ma blokow, wiec wpisuje te stala. Zadne wskazanie nie ma tu wlasnej
 * wartosci.
 */
export const BLOCK_KEY_WSKAZANIA = "wskazania";

/** LensFinding.severity. */
export type Severity = "INFO" | "WARN" | "ERROR";

/**
 * LensFinding.severity tez jest kolumna WYMAGANA. Wynika wprost z rodzaju
 * wskazania: mocna strona to INFO, rzecz do poprawy to WARN. ERROR zostaje
 * nieuzywany (nic w ocenie wg metodyki nie jest "bledem" w sensie awarii).
 */
export function severityDlaRodzaju(rodzaj: FindingKind): Severity {
  return rodzaj === "mocna-strona" ? "INFO" : "WARN";
}

// ---------------------------------------------------------------------------
// Role mowcow
// ---------------------------------------------------------------------------

/** Speaker.role. INNY to wartosc domyslna, dopoki nic jej nie ustawi. */
export const ROLE_MOWCOW = ["SPRZEDAWCA", "KLIENT", "INNY"] as const;
export type SpeakerRole = (typeof ROLE_MOWCOW)[number];

export function czyRolaMowcy(wartosc: unknown): wartosc is SpeakerRole {
  return typeof wartosc === "string" && (ROLE_MOWCOW as readonly string[]).includes(wartosc);
}

// ---------------------------------------------------------------------------
// Wejscie tekstowe - kontrakt miedzy formularzem a serwerem
// ---------------------------------------------------------------------------

/**
 * Najdluzszy tekst, jaki przyjmujemy (w znakach, PO normalizacji przez parser).
 *
 * Dwugodzinne spotkanie to ok. 40 tys. tokenow wejscia, wiec 400 tys. znakow
 * (kilkakrotnie wiecej niz najdluzsza rozmowa, jaka mamy) zostawia spory zapas
 * pod progiem 272 tys. tokenow wejscia modelu tekstowego, takze przy polskich
 * znakach i czasach przy kazdej linii. Pole tekstowe w multipart zajmuje przy
 * tym ponizej 1 MB, czyli miesci sie w domyslnym limicie multera (fieldSize).
 * Jawny limit jest po to, zeby przeslanie wielkiego pliku konczylo sie czytelnym
 * bledem, a nie 500.
 */
export const MAX_ZNAKOW_TEKSTU = 400_000;

/** Najdluzszy opis rozmowy (w znakach). */
export const MAX_ZNAKOW_OPISU = 1000;

/**
 * Nazwy pol POST /api/sessions w trybie tekstowym (multipart/form-data, jak przy
 * nagraniu). Jedno z dwoch: plik `transcript` (.txt, UTF-8) ALBO pole `text`.
 * Pole `audio` w trybie tekstowym nie wystepuje.
 */
export const POLA_WEJSCIA_TEKSTOWEGO = {
  rodzajWejscia: "inputKind",
  plik: "transcript",
  tekst: "text",
  opis: "context",
  zgoda: "consent",
  soczewki: "lensSlugs",
  tytul: "title",
} as const;

/**
 * Wejscie tekstowe w postaci uporzadkowanej, juz po zdekodowaniu pol formularza.
 * To jest to, co formularz zbiera i co serwer odtwarza z multiparta.
 */
export interface WejscieTekstowe {
  /** Wklejony tekst albo zawartosc pliku .txt po zdekodowaniu z UTF-8. */
  tekst: string;
  /** Opis rozmowy. Obowiazkowy tylko wtedy, gdy parser zwroci BEZ_PODPISOW. */
  opis: string | null;
  /** Zgoda rozmowcow. Wymagana przy KAZDYM wejsciu, takze tekstowym. */
  zgoda: boolean;
  /** Slugi wybranych parametrow oceny. Nie moze byc pusta. */
  soczewki: string[];
  /** Tytul sesji; brak znaczy "wez z nazwy pliku albo domyslny". */
  tytul?: string;
}

/**
 * Odpowiedz serwera na przyjecie tekstu (202), obok pol wspolnych z nagraniem
 * (id, title, status).
 *
 * `podpisany` mowi, ktora sciezka poszla: true - mowcy i wypowiedzi sa juz
 * zapisane, sesja idzie prosto do oceny; false - tekst czeka na podzial przez
 * model (status SPLITTING, potem SPLIT_REVIEW).
 */
export interface OdpowiedzPrzyjeciaTekstu {
  id: string;
  title: string;
  status: SessionStatus;
  podpisany: boolean;
  liczbaMowcow: number | null;
  liczbaWypowiedzi: number | null;
}

/**
 * Pole z lista slugow przychodzi z formularza w kilku postaciach: powtorzone
 * pole multiparta (tablica tekstow), tekst z tablica JSON-a albo tekst z
 * wartosciami po przecinku. Ta funkcja sprowadza wszystkie do tablicy.
 *
 * Zwraca null, gdy pole ma zly ksztalt (np. obiekt, liczba, JSON, ktory nie jest
 * tablica tekstow) - to jest blad wejscia, a nie "pusty wybor". Pusta tablica
 * oznacza, ze pole bylo, ale nic nie zaznaczono. Powtorzenia i puste pozycje
 * znikaja, kolejnosc zostaje.
 */
export function wczytajListeSlugow(surowe: unknown): string[] | null {
  let pozycje: unknown[];

  if (surowe === undefined || surowe === null) {
    return [];
  } else if (Array.isArray(surowe)) {
    pozycje = surowe;
  } else if (typeof surowe === "string") {
    const tekst = surowe.trim();
    if (tekst === "") return [];
    if (tekst.startsWith("[")) {
      try {
        const zJsona: unknown = JSON.parse(tekst);
        if (!Array.isArray(zJsona)) return null;
        pozycje = zJsona;
      } catch {
        return null;
      }
    } else {
      pozycje = tekst.split(",");
    }
  } else {
    return null;
  }

  const wynik: string[] = [];
  for (const p of pozycje) {
    if (typeof p !== "string") return null;
    const slug = p.trim();
    if (slug === "") continue;
    // Slug ma ten sam ksztalt co w methodology/lenses/*.json. Cos, co nim nie jest
    // (np. obiekt JSON podany jako tekst), to zly ksztalt pola, a nie "nieznany parametr".
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return null;
    if (!wynik.includes(slug)) wynik.push(slug);
  }
  return wynik;
}
